import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { DEFAULT_PAGE_SIZE } from 'constants/tables';
import { useGetWallet } from 'services/reactQueryHooks/wallet';
import { mapWalletToDashboard } from './helper';

const useWalletPage = () => {
  const { sendAmplitudeEvent } = useAmplitude();
  const corporateClientId = useSelector(state => state.user.corporateClientId);
  const pasAccountId = useSelector(
    state => state.user.currentCorporation?.walletPaymentAccountIds?.[0],
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [transactionType, setTransactionType] = useState([]);
  const [transactionStatus, setTransactionStatus] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  // Freeze/close are still mocked (FS-184 / FS-186), so the server status does not move after them.
  // The footer reports the new status here and the whole dashboard follows it. Once the real
  // endpoints invalidate the wallet query, the refetched status replaces this and it can go.
  const [statusOverride, setStatusOverride] = useState(null);

  useEffect(() => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: wallet, isLoading, isFetching, isPreviousData, isError, refetch } = useGetWallet({
    corporateClientId,
    pasAccountId,
    pageNumber,
    pageSize,
    transactionType,
    status: transactionStatus,
    orderBy,
  });

  const serverStatus = wallet?.status;

  // A status change coming from the server wins over anything the footer reported locally.
  useEffect(() => {
    setStatusOverride(null);
  }, [serverStatus]);

  const dashboard = useMemo(() => {
    const mapped = mapWalletToDashboard(wallet);
    if (!mapped || !statusOverride) {
      return mapped;
    }
    return {
      ...mapped,
      status: statusOverride.status,
      statusChangedAt: statusOverride.statusChangedAt ?? null,
    };
  }, [wallet, statusOverride]);

  const handleTransactionTypeChange = nextTransactionType => {
    setTransactionType(nextTransactionType ?? []);
    setPageNumber(1);
  };

  const handleTransactionStatusChange = nextTransactionStatus => {
    setTransactionStatus(nextTransactionStatus ?? []);
    setPageNumber(1);
  };

  const handleOrderByChange = nextOrderBy => {
    setOrderBy(nextOrderBy ?? null);
    setPageNumber(1);
  };

  const handlePageSizeChange = (_current, size) => {
    setPageSize(size);
  };

  const handleStatusChange = ({ status, statusChangedAt } = {}) => {
    if (!status) {
      return;
    }
    setStatusOverride({ status, statusChangedAt: statusChangedAt ?? null });
  };

  return {
    isLoading,
    isHistoryFetching: isFetching && isPreviousData,
    isError,
    wallet,
    dashboard,
    pageNumber,
    pageSize,
    transactionType,
    transactionStatus,
    orderBy,
    onPageChange: setPageNumber,
    onPageSizeChange: handlePageSizeChange,
    onTransactionTypeChange: handleTransactionTypeChange,
    onTransactionStatusChange: handleTransactionStatusChange,
    onOrderByChange: handleOrderByChange,
    onStatusChange: handleStatusChange,
    onRetry: refetch,
  };
};

export default useWalletPage;
